import React, { useMemo, useState } from "react";

// Lightweight, dependency-free emoji picker.
// Renders the inner panel only — callers wrap it in their own Popover/trigger,
// so the same picker serves both the composer (LOT 3) and reactions (LOT 4).
//
// Each entry is [emoji, "space separated search keywords"].
const CATEGORIES = [
  {
    id: "people", label: "Fețe și oameni", tab: "😀",
    items: [
      ["😀", "grin smile happy"], ["😁", "grin beaming"], ["😂", "joy laugh tears"],
      ["🤣", "rofl laughing"], ["😊", "blush smile happy"], ["😇", "angel innocent"],
      ["🙂", "slight smile"], ["😉", "wink"], ["😍", "love heart eyes"],
      ["😘", "kiss blow"], ["😗", "kiss"], ["🤔", "thinking hmm"],
      ["🤨", "skeptic raised brow"], ["😐", "neutral"], ["😴", "sleep tired"],
      ["😎", "cool sunglasses"], ["🥳", "party celebrate"], ["😢", "cry sad tear"],
      ["😭", "sob cry"], ["😅", "sweat nervous"], ["😳", "flushed embarrassed"], ["😮", "wow surprised"],
      ["😱", "scream shock"], ["😡", "angry mad rage"], ["🤯", "mind blown"],
      ["🙄", "eye roll"], ["😬", "grimace"], ["🤗", "hug"],
      ["🤩", "star struck excited"], ["🥺", "pleading puppy"], ["😏", "smirk"],
      ["🤝", "handshake deal"], ["🙏", "pray thanks please"], ["👋", "wave hello hi bye"],
    ],
  },
  {
    id: "gestures", label: "Gesturi", tab: "👍",
    items: [
      ["👍", "thumbs up like yes approve"], ["👎", "thumbs down dislike no"],
      ["👌", "ok perfect"], ["✌️", "peace victory"], ["🤞", "fingers crossed luck"],
      ["🤘", "rock horns"], ["🤙", "call shaka"], ["👏", "clap applause bravo"],
      ["🙌", "raise hands praise hooray"], ["💪", "muscle strong flex"],
      ["✋", "stop hand raise"], ["🖐️", "hand five"], ["👊", "fist bump punch"],
      ["✊", "fist power"], ["☝️", "point up index"], ["👇", "point down"],
      ["👈", "point left"], ["👉", "point right"], ["🫡", "salute respect"],
    ],
  },
  {
    id: "hearts", label: "Inimi și simboluri", tab: "❤️",
    items: [
      ["❤️", "red heart love"], ["🧡", "orange heart"], ["💛", "yellow heart"],
      ["💚", "green heart"], ["💙", "blue heart"], ["💜", "purple heart"],
      ["🖤", "black heart"], ["🤍", "white heart"], ["💔", "broken heart"],
      ["💯", "hundred perfect score"], ["✨", "sparkles shine"], ["⭐", "star"],
      ["🌟", "glowing star"], ["⚡", "zap lightning fast"], ["🔥", "fire lit hot"],
      ["💥", "boom collision"], ["✅", "check done yes ok"], ["❌", "cross no wrong"],
      ["❓", "question"], ["❗", "exclamation important"], ["⚠️", "warning caution"],
    ],
  },
  {
    id: "nature", label: "Animale și natură", tab: "🐶",
    items: [
      ["🐶", "dog puppy"], ["🐱", "cat kitten"], ["🦊", "fox"], ["🐻", "bear"],
      ["🐼", "panda"], ["🐨", "koala"], ["🦁", "lion"], ["🐯", "tiger"],
      ["🐸", "frog"], ["🐵", "monkey"], ["🦄", "unicorn"], ["🐝", "bee"],
      ["🦋", "butterfly"], ["🐢", "turtle slow"], ["🐙", "octopus"], ["🐳", "whale"],
      ["🌳", "tree"], ["🌵", "cactus"], ["🌹", "rose flower"], ["🌻", "sunflower"],
      ["🌈", "rainbow"], ["🌙", "moon night"], ["☀️", "sun sunny"], ["🌊", "wave ocean water"],
    ],
  },
  {
    id: "food", label: "Mâncare și băutură", tab: "🍕",
    items: [
      ["🍕", "pizza"], ["🍔", "burger"], ["🍟", "fries"], ["🌮", "taco"],
      ["🍣", "sushi"], ["🍜", "ramen noodles"], ["🍩", "donut"], ["🍪", "cookie"],
      ["🎂", "cake birthday"], ["🍰", "cake slice"], ["🍫", "chocolate"], ["🍿", "popcorn"],
      ["☕", "coffee tea"], ["🍵", "tea"], ["🍺", "beer"], ["🍷", "wine"],
      ["🥂", "cheers toast champagne"], ["🍎", "apple"], ["🍌", "banana"], ["🍓", "strawberry"],
    ],
  },
  {
    id: "activity", label: "Activități și călătorii", tab: "⚽",
    items: [
      ["⚽", "soccer football"], ["🏀", "basketball"], ["🏈", "football"], ["🎾", "tennis"],
      ["🏆", "trophy win champion"], ["🥇", "gold medal first"], ["🎯", "target bullseye dart"],
      ["🎮", "game controller"], ["🎲", "dice"], ["🎉", "party tada celebrate"],
      ["🎊", "confetti"], ["🎁", "gift present"], ["🚀", "rocket launch ship"],
      ["✈️", "plane travel flight"], ["🚗", "car"], ["🏠", "house home"],
      ["🌍", "earth world globe"], ["🗺️", "map"], ["⛰️", "mountain"], ["🏖️", "beach vacation"],
    ],
  },
  {
    id: "objects", label: "Obiecte", tab: "💡",
    items: [
      ["💡", "idea bulb light"], ["🔋", "battery"], ["💻", "laptop computer"],
      ["🖥️", "desktop monitor"], ["📱", "phone mobile"], ["⌨️", "keyboard"],
      ["🖱️", "mouse"], ["💾", "save floppy disk"], ["📦", "package box ship"],
      ["📌", "pin"], ["📎", "paperclip"], ["✏️", "pencil edit write"],
      ["📝", "memo note"], ["📚", "books"], ["🔍", "search magnify"], ["🔑", "key"],
      ["🔒", "lock secure"], ["🔓", "unlock"], ["🛠️", "tools fix"], ["⚙️", "gear settings config"],
      ["🐛", "bug issue"], ["⏰", "alarm clock time"], ["📈", "chart up growth"], ["💰", "money cash"],
    ],
  },
];

const RECENTS_KEY = "vs_emoji_recents";
const MAX_RECENTS = 24;

function loadRecents() {
  try {
    const v = JSON.parse(localStorage.getItem(RECENTS_KEY) || "[]");
    return Array.isArray(v) ? v.filter((e) => typeof e === "string").slice(0, MAX_RECENTS) : [];
  } catch {
    return [];
  }
}

function pushRecent(emoji) {
  try {
    const next = [emoji, ...loadRecents().filter((e) => e !== emoji)].slice(0, MAX_RECENTS);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch { /* ignore */ }
}

export default function EmojiPicker({ onSelect }) {
  const [active, setActive] = useState(CATEGORIES[0].id);
  const [query, setQuery] = useState("");
  const recents = useMemo(loadRecents, []);

  const choose = (emoji) => {
    pushRecent(emoji);
    onSelect?.(emoji);
  };

  const q = query.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!q) return null;
    const seen = new Set();
    const out = [];
    for (const cat of CATEGORIES) {
      for (const [emoji, kw] of cat.items) {
        if (seen.has(emoji)) continue;
        if (kw.includes(q)) { out.push(emoji); seen.add(emoji); }
      }
    }
    return out;
  }, [q]);

  const activeItems = CATEGORIES.find((c) => c.id === active)?.items || [];

  return (
    <div className="w-[268px] select-none" data-testid="emoji-picker">
      <div className="p-2 border-b border-line/10">
        <input
          data-testid="emoji-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Caută emoji"
          className="w-full bg-surface-2 border border-line/10 text-fg text-xs px-2 py-1.5 outline-none focus:border-line/30 placeholder-fg/30"
        />
      </div>

      {searchResults ? (
        <div className="p-2">
          {searchResults.length === 0 ? (
            <p className="text-fg/40 text-xs py-6 text-center">Niciun emoji găsit</p>
          ) : (
            <div className="grid grid-cols-7 gap-0.5 max-h-[180px] overflow-y-auto">
              {searchResults.map((e) => <EmojiBtn key={e} emoji={e} onClick={choose} />)}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="p-2">
            {recents.length > 0 && (
              <div className="mb-2">
                <p className="label-tiny px-0.5 pb-1">// RECENTE</p>
                <div className="grid grid-cols-7 gap-0.5">
                  {recents.slice(0, 14).map((e) => <EmojiBtn key={`r-${e}`} emoji={e} onClick={choose} />)}
                </div>
              </div>
            )}
            <div className="grid grid-cols-7 gap-0.5 max-h-[180px] overflow-y-auto">
              {activeItems.map(([e]) => <EmojiBtn key={e} emoji={e} onClick={choose} />)}
            </div>
          </div>
          <div className="flex border-t border-line/10">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                type="button"
                title={cat.label}
                aria-label={cat.label}
                data-testid={`emoji-cat-${cat.id}`}
                onClick={() => setActive(cat.id)}
                className={`flex-1 py-1.5 text-base ${active === cat.id ? "bg-fg/10" : "hover:bg-fg/5"}`}
              >{cat.tab}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function EmojiBtn({ emoji, onClick }) {
  return (
    <button
      type="button"
      data-testid={`emoji-${emoji}`}
      onClick={() => onClick(emoji)}
      className="aspect-square flex items-center justify-center text-lg hover:bg-fg/10 rounded-none"
    >{emoji}</button>
  );
}
